import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Button,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Alert,
  CircularProgress,
  InputAdornment,
  TextField,
  Grid,
  Card,
  CardContent,
  useTheme,
  useMediaQuery,
  alpha,
  Stack,
  TablePagination,
  TableSortLabel,
  Select,
  FormControl,
  InputLabel,
  MenuItem,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Divider,
} from '@mui/material';
import {
  Search,
  Refresh,
  Email,
  Sms,
  WhatsApp,
  NotificationsActive,
  CheckCircle,
  Error,
  Schedule,
  Replay,
  Visibility,
  Delete,
  Send,
  FilterList as FilterListIcon,
  MailOutline as MailOutlineIcon,
} from '@mui/icons-material';
import { Helmet } from 'react-helmet-async';
import Swal from 'sweetalert2';
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { formatarData, formatarDataHora } from '../utils/formatToView';

import mensagemService, { Mensagem, MensagemFilters } from '../services/mensagemService';
import WhatsappViewMsg from '../components/modals/WhatsappViewMsg';
import EmailViewMsg from '../components/modals/EmailViewMsg';
import SmsViewMsg from '../components/modals/SmsViewMsg';
import SysKpiCard from '../components/common/SysKpiCard';
import SysDateRangePicker from '../components/SysDateRangePicker';
import {
  pageShellSx,
  rankingHeaderPaperSx,
  filtersPaperSx,
  listSectionPaperSx,
  listSectionHeaderBarSx,
  rankingTableHeadRowSx,
  tablePaginationWrapperSx,
} from '../styles/theme';

type DateRange = { startDate: Date | null; endDate: Date | null };

const emptyDateRange: DateRange = { startDate: null, endDate: null };

const dateToYmd = (date: Date | null): string | undefined => {
  if (!date) return undefined;
  const y = date.getFullYear();
  const m = String(date.getMonth() + 1).padStart(2, '0');
  const d = String(date.getDate()).padStart(2, '0');
  return `${y}-${m}-${d}`;
};

const applyDateRangeToFilters = (
  filters: MensagemFilters,
  prefix: 'criacao' | 'agendamento' | 'envio',
  range: DateRange
) => {
  const inicio = dateToYmd(range.startDate);
  const fim = dateToYmd(range.endDate);
  if (!inicio || !fim) return;

  if (prefix === 'criacao') {
    filters.criacao_inicio = inicio;
    filters.criacao_fim = fim;
  } else if (prefix === 'agendamento') {
    filters.agendamento_inicio = inicio;
    filters.agendamento_fim = fim;
  } else {
    filters.envio_inicio = inicio;
    filters.envio_fim = fim;
  }
};

const MensagensPage: React.FC = () => {
  const navigate = useNavigate();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  // Estados
  const [mensagens, setMensagens] = useState<Mensagem[]>([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(50);
  const [totalMensagens, setTotalMensagens] = useState(0);
  const [totalPages, setTotalPages] = useState(0);

  // Estados para filtros
  const [statusFilter, setStatusFilter] = useState<string>('');
  const [tipoFilter, setTipoFilter] = useState<string>('');
  const [eventoFilter, setEventoFilter] = useState<string>('');
  const [criacaoRange, setCriacaoRange] = useState<DateRange>(emptyDateRange);
  const [agendamentoRange, setAgendamentoRange] = useState<DateRange>(emptyDateRange);
  const [envioRange, setEnvioRange] = useState<DateRange>(emptyDateRange);

  // Estados para estatísticas
  const [stats, setStats] = useState<any>(null);
  const [loadingStats, setLoadingStats] = useState(false);

  // Estado para mensagem selecionada
  const [selectedMensagem, setSelectedMensagem] = useState<Mensagem | null>(null);
  const [detailsDialogOpen, setDetailsDialogOpen] = useState(false);
  const [whatsappViewOpen, setWhatsappViewOpen] = useState(false);
  const [emailViewOpen, setEmailViewOpen] = useState(false);
  const [smsViewOpen, setSmsViewOpen] = useState(false);

  // Ordenação
  const [orderBy, setOrderBy] = useState<string>('createdAt');
  const [order, setOrder] = useState<'asc' | 'desc'>('desc');

  // Carregar mensagens
  const loadMensagens = async () => {
    try {
      setLoading(true);
      setError(null);

      const filters: MensagemFilters = {
        page: page + 1,
        limit: rowsPerPage,
        sortBy: orderBy,
        sortOrder: order.toUpperCase() as 'ASC' | 'DESC',
      };

      if (statusFilter) filters.status = statusFilter as any;
      if (tipoFilter) filters.tipo = tipoFilter as any;
      if (eventoFilter) filters.evento = eventoFilter;
      if (searchTerm) filters.search = searchTerm;
      applyDateRangeToFilters(filters, 'criacao', criacaoRange);
      applyDateRangeToFilters(filters, 'agendamento', agendamentoRange);
      applyDateRangeToFilters(filters, 'envio', envioRange);

      const response = await mensagemService.getMensagens(filters);

      if (response.success && response.data) {
        setMensagens(response.data);
        setTotalMensagens(response.pagination?.total || 0);
        setTotalPages(response.pagination?.pages || 0);
      } else {
        setError(response.message || 'Erro ao carregar mensagens');
      }
    } catch (error) {
      console.error('Erro ao carregar mensagens:', error);
      setError('Erro ao carregar mensagens');
    } finally {
      setLoading(false);
    }
  };

  // Carregar estatísticas
  const loadStats = async () => {
    try {
      setLoadingStats(true);
      const response = await mensagemService.getStats(
        dateToYmd(criacaoRange.startDate),
        dateToYmd(criacaoRange.endDate)
      );

      if (response.success && response.data) {
        setStats(response.data);
      }
    } catch (error) {
      console.error('Erro ao carregar estatísticas:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  // Enviar mensagem agendada imediatamente
  const handleSendNow = async (id: number) => {
    try {
      const result = await Swal.fire({
        title: 'Enviar mensagem agora?',
        text: 'A mensagem será enviada imediatamente',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, enviar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: theme.palette.primary.main,
      });

      if (result.isConfirmed) {
        const response = await mensagemService.sendNowMensagem(id);

        if (response.outcome === 'cancelado') {
          Swal.fire({
            icon: 'info',
            title: 'Mensagem cancelada',
            text: response.message || 'A mensagem foi cancelada pois a fatura vinculada não está mais pendente.',
          });
          loadMensagens();
        } else if (response.outcome === 'reagendado') {
          Swal.fire({
            icon: 'info',
            title: 'Mensagem reagendada',
            text: response.message || 'A mensagem foi reagendada pois o contrato está em observação.',
          });
          loadMensagens();
        } else if (response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Sucesso!',
            text: 'Mensagem enviada com sucesso',
            timer: 2000,
          });
          loadMensagens();
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erro!',
            text: response.message || 'Erro ao enviar mensagem',
          });
        }
      }
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: error?.response?.data?.message || error?.message || 'Erro ao enviar mensagem',
      });
    }
  };

  // Reenviar mensagem
  const handleResend = async (id: number) => {
    try {
      const result = await Swal.fire({
        title: 'Reenviar mensagem?',
        text: 'A mensagem será marcada para reenvio',
        icon: 'question',
        showCancelButton: true,
        confirmButtonText: 'Sim, reenviar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: theme.palette.primary.main,
      });

      if (result.isConfirmed) {
        const response = await mensagemService.resendMensagem(id);

        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Sucesso!',
            text: 'Mensagem marcada para reenvio',
            timer: 2000,
          });
          loadMensagens();
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erro!',
            text: response.message || 'Erro ao reenviar mensagem',
          });
        }
      }
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: error?.message || 'Erro ao reenviar mensagem',
      });
    }
  };

  // Deletar mensagem
  const handleDelete = async (id: number) => {
    try {
      const result = await Swal.fire({
        title: 'Deletar mensagem?',
        text: 'Esta ação não pode ser desfeita',
        icon: 'warning',
        showCancelButton: true,
        confirmButtonText: 'Sim, deletar',
        cancelButtonText: 'Cancelar',
        confirmButtonColor: theme.palette.error.main,
      });

      if (result.isConfirmed) {
        const response = await mensagemService.deleteMensagem(id);

        if (response.success) {
          Swal.fire({
            icon: 'success',
            title: 'Deletado!',
            text: 'Mensagem deletada com sucesso',
            timer: 2000,
          });
          loadMensagens();
        } else {
          Swal.fire({
            icon: 'error',
            title: 'Erro!',
            text: response.message || 'Erro ao deletar mensagem',
          });
        }
      }
    } catch (error: any) {
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: error?.message || 'Erro ao deletar mensagem',
      });
    }
  };

  // Ver detalhes
  const handleViewDetails = async (id: number) => {
    try {
      const response = await mensagemService.getMensagemById(id);

      if (response.success && response.data) {
        setSelectedMensagem(response.data);

        // Abre o modal apropriado baseado no tipo da mensagem
        switch (response.data.tipo) {
          case 'whatsapp':
            setWhatsappViewOpen(true);
            break;
          case 'email':
            setEmailViewOpen(true);
            break;
          case 'sms':
            setSmsViewOpen(true);
            break;
          default:
            // Para aplicativo ou outros tipos, abre o modal de detalhes padrão
            setDetailsDialogOpen(true);
            break;
        }
      }
    } catch (error) {
      Swal.fire({
        icon: 'error',
        title: 'Erro!',
        text: 'Erro ao carregar detalhes da mensagem',
      });
    }
  };

  // Efeitos
  useEffect(() => {
    setPage(0);
  }, [
    criacaoRange.startDate,
    criacaoRange.endDate,
    agendamentoRange.startDate,
    agendamentoRange.endDate,
    envioRange.startDate,
    envioRange.endDate,
  ]);

  useEffect(() => {
    loadMensagens();
  }, [
    page,
    rowsPerPage,
    statusFilter,
    tipoFilter,
    eventoFilter,
    orderBy,
    order,
    criacaoRange.startDate,
    criacaoRange.endDate,
    agendamentoRange.startDate,
    agendamentoRange.endDate,
    envioRange.startDate,
    envioRange.endDate,
  ]);

  useEffect(() => {
    loadStats();
  }, [criacaoRange.startDate, criacaoRange.endDate]);

  useEffect(() => {
    const delayDebounce = setTimeout(() => {
      if (page === 0) {
        loadMensagens();
      } else {
        setPage(0);
      }
    }, 500);

    return () => clearTimeout(delayDebounce);
  }, [searchTerm]);

  // Funções auxiliares
  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'enviado':
        return <CheckCircle fontSize="small" />;
      case 'aguardando_envio':
        return <Schedule fontSize="small" />;
      case 'enfileirado':
        return <Schedule fontSize="small" />;
      case 'falhou':
        return <Error fontSize="small" />;
      default:
        return null;
    }
  };

  const getStatusColor = (status: string): 'success' | 'warning' | 'error' | 'default' | 'info' => {
    switch (status) {
      case 'enviado':
        return 'success';
      case 'aguardando_envio':
        return 'warning';
      case 'enfileirado':
        return 'info';
      case 'falhou':
        return 'error';
      default:
        return 'default';
    }
  };

  const getTipoIcon = (tipo: string) => {
    switch (tipo) {
      case 'email':
        return <Email fontSize="small" />;
      case 'sms':
        return <Sms fontSize="small" />;
      case 'whatsapp':
        return <WhatsApp fontSize="small" />;
      case 'aplicativo':
        return <NotificationsActive fontSize="small" />;
      default:
        return null;
    }
  };

  const getTipoColor = (tipo: string): 'primary' | 'secondary' | 'success' | 'info' | 'default' => {
    switch (tipo) {
      case 'email':
        return 'primary';
      case 'sms':
        return 'secondary';
      case 'whatsapp':
        return 'success';
      case 'aplicativo':
        return 'info';
      default:
        return 'default';
    }
  };

  const formatStatus = (status: string) => {
    const statusMap: Record<string, string> = {
      enviado: 'Enviado',
      aguardando_envio: 'Aguardando Envio',
      enfileirado: 'Na fila de envio',
      falhou: 'Falhou',
    };
    return statusMap[status] || status;
  };

  const formatTipo = (tipo: string) => {
    const tipoMap: Record<string, string> = {
      email: 'E-mail',
      sms: 'SMS',
      whatsapp: 'WhatsApp',
      aplicativo: 'Aplicativo',
    };
    return tipoMap[tipo] || tipo;
  };

  const formatEvento = (evento: string | null | undefined) => {
    if (!evento) return '-';
    const eventoMap: Record<string, string> = {
      aniversario_cliente: 'Aniversário Cliente',
      lembrete_primeiro_envio_antes: 'Lembrete (1º antes)',
      lembrete_segundo_envio_antes: 'Lembrete (2º antes)',
      lembrete_5_antes: 'Lembrete (5 dias antes)',
      lembrete_10_antes: 'Lembrete (10 dias antes)',
      lembrete_dia_vencimento: 'Lembrete (Dia Vencimento)',
      lembrete_primeiro_envio_apos: 'Lembrete (1º após)',
      lembrete_segundo_envio_apos: 'Lembrete (2º após)',
      lembrete_5_apos: 'Lembrete (5 dias após)',
      lembrete_10_apos: 'Lembrete (10 dias após)',
      fatura_criada: 'Fatura Criada',
      confirmacao_pagamento: 'Confirmação Pagamento',
      novo_chamado: 'Novo Chamado',
      boas_vindas: 'Boas Vindas',
    };
    return eventoMap[evento] || evento;
  };

  const formatDate = (dateString: string | null | undefined) => {
    if (!dateString) return '-';
    try {
      return format(new Date(dateString), "dd/MM/yyyy 'às' HH:mm", { locale: ptBR });
    } catch {
      return dateString;
    }
  };


  const countByStatus = (status: string) => stats?.porStatus?.find((s: any) => s.status === status)?.total || 0;

  // Renderização
  return (
    <Box sx={pageShellSx}>
      <Helmet>
        <title>Mensagens - SysUp</title>
      </Helmet>

      <Paper elevation={0} sx={rankingHeaderPaperSx(theme, 'primary')}>
        <Box sx={{ position: 'absolute', top: -20, right: -10, opacity: 0.08, transform: 'rotate(15deg)' }}>
          <Send sx={{ fontSize: 120, color: theme.palette.primary.main }} />
        </Box>
        <Stack direction={{ xs: 'column', sm: 'row' }} alignItems={{ xs: 'flex-start', sm: 'center' }} justifyContent="space-between" spacing={1.25}>
          <Stack direction="row" alignItems="center" spacing={1.25}>
            <Avatar sx={{ width: 40, height: 40, background: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`, boxShadow: `0 4px 12px ${alpha(theme.palette.primary.main, 0.35)}` }}>
              <MailOutlineIcon sx={{ fontSize: 22 }} />
            </Avatar>
            <Box>
              <Typography variant="h6" fontWeight={800} lineHeight={1.2}>Mensagens</Typography>
              <Typography variant="caption" color="text.secondary">Histórico de envios por e-mail, SMS, WhatsApp e app</Typography>
            </Box>
          </Stack>
          <Button variant="contained" size="small" startIcon={<Refresh />} onClick={() => { loadMensagens(); loadStats(); }} disabled={loading}>Atualizar</Button>
        </Stack>
      </Paper>

      {!loadingStats && stats && (
        <Grid container spacing={1} mb={1.5}>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Total Geral" valor={stats.total || 0} icone={<Send sx={{ color: theme.palette.primary.main }} />} cor={theme.palette.primary.main} bgCor={alpha(theme.palette.primary.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Enviadas" valor={countByStatus('enviado')} icone={<CheckCircle sx={{ color: theme.palette.success.main }} />} cor={theme.palette.success.main} bgCor={alpha(theme.palette.success.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Aguardando" valor={countByStatus('aguardando_envio')} icone={<Schedule sx={{ color: theme.palette.warning.main }} />} cor={theme.palette.warning.main} bgCor={alpha(theme.palette.warning.main, 0.12)} /></Grid>
          <Grid item xs={6} md={3}><SysKpiCard titulo="Falhas" valor={countByStatus('falhou')} icone={<Error sx={{ color: theme.palette.error.main }} />} cor={theme.palette.error.main} bgCor={alpha(theme.palette.error.main, 0.12)} /></Grid>
        </Grid>
      )}

      <Paper elevation={0} sx={filtersPaperSx(theme)}>
        <Stack direction="row" alignItems="center" spacing={0.75} mb={1.25}>
          <FilterListIcon sx={{ fontSize: 18, color: theme.palette.primary.main }} />
          <Typography variant="body2" fontWeight={700}>Filtros</Typography>
        </Stack>
        <Grid container spacing={1}>
          <Grid item xs={12} sm={6} md={3}>
            <TextField
              fullWidth
              label="Buscar"
              variant="outlined"
              size='small'
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search />
                  </InputAdornment>
                ),
              }}
            />
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                label="Status"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="enviado">Enviado</MenuItem>
                <MenuItem value="aguardando_envio">Aguardando Envio</MenuItem>
                <MenuItem value="enfileirado">Na fila de envio</MenuItem>
                <MenuItem value="falhou">Falhou</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={2}>
            <FormControl fullWidth size="small">
              <InputLabel>Tipo</InputLabel>
              <Select
                value={tipoFilter}
                onChange={(e) => setTipoFilter(e.target.value)}
                label="Tipo"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="email">E-mail</MenuItem>
                <MenuItem value="sms">SMS</MenuItem>
                <MenuItem value="whatsapp">WhatsApp</MenuItem>
                <MenuItem value="aplicativo">Aplicativo</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} sm={6} md={3}>
            <FormControl fullWidth size="small">
              <InputLabel>Evento</InputLabel>
              <Select
                value={eventoFilter}
                onChange={(e) => setEventoFilter(e.target.value)}
                label="Evento"
              >
                <MenuItem value="">Todos</MenuItem>
                <MenuItem value="aniversario_cliente">Aniversário Cliente</MenuItem>
                <MenuItem value="lembrete_primeiro_envio_antes">Lembrete (1º antes)</MenuItem>
                <MenuItem value="lembrete_segundo_envio_antes">Lembrete (2º antes)</MenuItem>
                <MenuItem value="lembrete_dia_vencimento">Lembrete (Dia Vencimento)</MenuItem>
                <MenuItem value="lembrete_primeiro_envio_apos">Lembrete (1º após)</MenuItem>
                <MenuItem value="lembrete_segundo_envio_apos">Lembrete (2º após)</MenuItem>
                <MenuItem value="fatura_criada">Fatura Criada</MenuItem>
                <MenuItem value="confirmacao_pagamento">Confirmação Pagamento</MenuItem>
                <MenuItem value="novo_chamado">Novo Chamado</MenuItem>
                <MenuItem value="boas_vindas">Boas Vindas</MenuItem>
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <SysDateRangePicker
              label="Data Criação"
              value={criacaoRange}
              onChange={setCriacaoRange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SysDateRangePicker
              label="Data Agendamento"
              value={agendamentoRange}
              onChange={setAgendamentoRange}
            />
          </Grid>
          <Grid item xs={12} md={4}>
            <SysDateRangePicker
              label="Data Envio"
              value={envioRange}
              onChange={setEnvioRange}
            />
          </Grid>
        </Grid>
      </Paper>

      {error && <Alert severity="error" sx={{ mb: 1.5 }}>{error}</Alert>}

      <Paper elevation={0} sx={listSectionPaperSx(theme)}>
        <Box sx={listSectionHeaderBarSx(theme)}>
          <Typography variant="body2" fontWeight={700}>Listagem de Mensagens</Typography>
          <Chip size="small" label={`${totalMensagens} mensagem${totalMensagens !== 1 ? 'ns' : ''}`} color="primary" variant="outlined" sx={{ height: 22, fontSize: '0.7rem' }} />
        </Box>

        {loading ? (
          <Box sx={{ p: 3, display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
            <CircularProgress size={32} />
            <Typography variant="caption" color="text.secondary">Carregando mensagens...</Typography>
          </Box>
        ) : mensagens.length === 0 ? (
          <Box sx={{ p: 2.5, textAlign: 'center' }}>
            <MailOutlineIcon sx={{ fontSize: 40, color: alpha(theme.palette.text.secondary, 0.25), mb: 1 }} />
            <Typography variant="subtitle2" fontWeight={600}>Nenhuma mensagem encontrada</Typography>
          </Box>
        ) : (
          <>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow sx={rankingTableHeadRowSx(theme)}>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'id'}
                    direction={orderBy === 'id' ? order : 'asc'}
                    onClick={() => {
                      const isAsc = orderBy === 'id' && order === 'asc';
                      setOrder(isAsc ? 'desc' : 'asc');
                      setOrderBy('id');
                    }}
                  >
                    ID
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'status'}
                    direction={orderBy === 'status' ? order : 'asc'}
                    onClick={() => {
                      const isAsc = orderBy === 'status' && order === 'asc';
                      setOrder(isAsc ? 'desc' : 'asc');
                      setOrderBy('status');
                    }}
                  >
                    Status
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'tipo'}
                    direction={orderBy === 'tipo' ? order : 'asc'}
                    onClick={() => {
                      const isAsc = orderBy === 'tipo' && order === 'asc';
                      setOrder(isAsc ? 'desc' : 'asc');
                      setOrderBy('tipo');
                    }}
                  >
                    Tipo
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'evento'}
                    direction={orderBy === 'evento' ? order : 'asc'}
                    onClick={() => {
                      const isAsc = orderBy === 'evento' && order === 'asc';
                      setOrder(isAsc ? 'desc' : 'asc');
                      setOrderBy('evento');
                    }}
                  >
                    Evento
                  </TableSortLabel>
                </TableCell>
                <TableCell>Destino</TableCell>
                <TableCell>Cliente</TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'createdAt'}
                    direction={orderBy === 'createdAt' ? order : 'asc'}
                    onClick={() => {
                      const isAsc = orderBy === 'createdAt' && order === 'asc';
                      setOrder(isAsc ? 'desc' : 'asc');
                      setOrderBy('createdAt');
                    }}
                  >
                    Data Criação
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'agendado_para'}
                    direction={orderBy === 'agendado_para' ? order : 'asc'}
                    onClick={() => {
                      const isAsc = orderBy === 'agendado_para' && order === 'asc';
                      setOrder(isAsc ? 'desc' : 'asc');
                      setOrderBy('agendado_para');
                    }}
                  >
                    Data Agendada
                  </TableSortLabel>
                </TableCell>
                <TableCell>
                  <TableSortLabel
                    active={orderBy === 'data_envio'}
                    direction={orderBy === 'data_envio' ? order : 'asc'}
                    onClick={() => {
                      const isAsc = orderBy === 'data_envio' && order === 'asc';
                      setOrder(isAsc ? 'desc' : 'asc');
                      setOrderBy('data_envio');
                    }}
                  >
                    Data Envio
                  </TableSortLabel>
                </TableCell>
                <TableCell>Tentativas</TableCell>
                <TableCell align="center">Ações</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
                {mensagens.map((mensagem) => (
                  <TableRow key={mensagem.id} hover sx={{ '& .MuiTableCell-root': { py: 0.6, px: 1 }, '&:hover': { bgcolor: alpha(theme.palette.primary.main, 0.04) } }}>
                    <TableCell>{mensagem.id}</TableCell>
                    <TableCell>
                      <Chip
                        icon={getStatusIcon(mensagem.status) || undefined}
                        label={formatStatus(mensagem.status)}
                        color={getStatusColor(mensagem.status) as any}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>
                      <Chip
                        icon={getTipoIcon(mensagem.tipo) || undefined}
                        label={formatTipo(mensagem.tipo)}
                        color={getTipoColor(mensagem.tipo) as any}
                        size="small"
                        variant="outlined"
                      />
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatEvento(mensagem.evento)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{mensagem.destino}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>
                      {mensagem.cliente ? mensagem.cliente.nome : '-'}
                    </TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{formatarDataHora(mensagem.createdAt)}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{mensagem.agendado_para ? formatarData(mensagem.agendado_para) : '-'}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }}>{mensagem.data_envio ? formatarDataHora(mensagem.data_envio) : '-'}</TableCell>
                    <TableCell>{mensagem.tentativas}</TableCell>
                    <TableCell sx={{ whiteSpace: 'nowrap' }} align="center">
                      <Stack direction="row" spacing={1} justifyContent="center">
                        <Tooltip title="Ver detalhes">
                          <IconButton
                            size="small"
                            color="primary"
                            onClick={() => handleViewDetails(mensagem.id)}
                          >
                            <Visibility fontSize="small" />
                          </IconButton>
                        </Tooltip>
                        {mensagem.status === 'aguardando_envio' && (
                          <Tooltip title="Enviar agora">
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleSendNow(mensagem.id)}
                            >
                              <Send fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        {mensagem.status === 'falhou' && (
                          <Tooltip title="Reenviar">
                            <IconButton
                              size="small"
                              color="warning"
                              onClick={() => handleResend(mensagem.id)}
                            >
                              <Replay fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title="Deletar">
                          <IconButton
                            size="small"
                            color="error"
                            onClick={() => handleDelete(mensagem.id)}
                          >
                            <Delete fontSize="small" />
                          </IconButton>
                        </Tooltip>
                      </Stack>
                    </TableCell>
                  </TableRow>
                ))}
            </TableBody>
          </Table>
        </TableContainer>
        {totalMensagens > 0 && (
          <>
          <Divider />
          <Box sx={tablePaginationWrapperSx(theme)}>
            <TablePagination
              component="div"
              size="small"
              count={totalMensagens}
              page={page}
              onPageChange={(_, newPage) => setPage(newPage)}
              rowsPerPage={rowsPerPage}
              onRowsPerPageChange={(e) => {
                setRowsPerPage(parseInt(e.target.value, 10));
                setPage(0);
              }}
              labelRowsPerPage="Linhas:"
              sx={{ '.MuiTablePagination-toolbar': { minHeight: 44, px: 1.5 }, '.MuiTablePagination-selectLabel, .MuiTablePagination-displayedRows': { fontSize: '0.75rem', my: 0 } }}
            />
          </Box>
          </>
        )}
          </>
        )}
      </Paper>

      {/* Dialog de Detalhes */}
      <Dialog
        open={detailsDialogOpen}
        onClose={() => setDetailsDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>Detalhes da Mensagem</DialogTitle>
        <DialogContent>
          {selectedMensagem && (
            <Box sx={{ pt: 2 }}>
              <Grid container spacing={2}>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    ID
                  </Typography>
                  <Typography variant="body1">{selectedMensagem.id}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Status
                  </Typography>
                  <Chip
                    icon={getStatusIcon(selectedMensagem.status) || undefined}
                    label={formatStatus(selectedMensagem.status)}
                    color={getStatusColor(selectedMensagem.status) as any}
                    size="small"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Tipo
                  </Typography>
                  <Chip
                    icon={getTipoIcon(selectedMensagem.tipo) || undefined}
                    label={formatTipo(selectedMensagem.tipo)}
                    color={getTipoColor(selectedMensagem.tipo) as any}
                    size="small"
                    variant="outlined"
                  />
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Evento
                  </Typography>
                  <Typography variant="body1">{formatEvento(selectedMensagem.evento)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Destino
                  </Typography>
                  <Typography variant="body1">{selectedMensagem.destino}</Typography>
                </Grid>
                <Grid item xs={12}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Conteúdo
                  </Typography>
                  <Paper sx={{ p: 2, mt: 1, bgcolor: 'grey.50' }}>
                    <Typography variant="body2" style={{ whiteSpace: 'pre-wrap' }}>
                      {selectedMensagem.conteudo}
                    </Typography>
                  </Paper>
                </Grid>
                {selectedMensagem.cliente && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Cliente
                    </Typography>
                    <Typography variant="body1">
                      {selectedMensagem.cliente.nome}
                    </Typography>
                  </Grid>
                )}
                {selectedMensagem.usuario && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="textSecondary">
                      Enviado por
                    </Typography>
                    <Typography variant="body1">
                      {selectedMensagem.usuario.nome} ({selectedMensagem.usuario.usuario})
                    </Typography>
                  </Grid>
                )}
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Data de Criação
                  </Typography>
                  <Typography variant="body1">{formatDate(selectedMensagem.createdAt)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Data de Envio
                  </Typography>
                  <Typography variant="body1">{formatDate(selectedMensagem.data_envio)}</Typography>
                </Grid>
                <Grid item xs={12} sm={6}>
                  <Typography variant="subtitle2" color="textSecondary">
                    Tentativas
                  </Typography>
                  <Typography variant="body1">{selectedMensagem.tentativas}</Typography>
                </Grid>
                {selectedMensagem.erro_mensagem && (
                  <Grid item xs={12}>
                    <Typography variant="subtitle2" color="error">
                      Erro
                    </Typography>
                    <Alert severity="error" sx={{ mt: 1 }}>
                      {selectedMensagem.erro_mensagem}
                    </Alert>
                  </Grid>
                )}
              </Grid>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDetailsDialogOpen(false)}>Fechar</Button>
        </DialogActions>
      </Dialog>

      {/* Modal de Visualização WhatsApp */}
      <WhatsappViewMsg
        open={whatsappViewOpen}
        onClose={() => setWhatsappViewOpen(false)}
        mensagem={selectedMensagem}
      />

      {/* Modal de Visualização Email */}
      <EmailViewMsg
        open={emailViewOpen}
        onClose={() => setEmailViewOpen(false)}
        mensagem={selectedMensagem}
      />

      {/* Modal de Visualização SMS */}
      <SmsViewMsg
        open={smsViewOpen}
        onClose={() => setSmsViewOpen(false)}
        mensagem={selectedMensagem}
      />
    </Box>
  );
};

export default MensagensPage;

